import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { ActionButtonVariants } from 'components/action-button';
import { Modal, ModalSize } from 'components/modal';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useLocationContext } from 'state/location';
import { ExternalOfferModalDetails } from 'state/loyalty/types';
import { blockContentToPlainText } from 'utils/sanity';

import {
  CTAButton,
  CloseButton,
  Container,
  ImageWrapper,
  MainTitle,
  OfferDescription,
  OfferImage,
  TextContentWrapper,
} from './external-incentive-modal.styled';

interface ModalExternalOfferRedeemProps {
  onChangeVisibility: VoidFunction;
  modalVisibility?: boolean | undefined;
  modalDetails: ExternalOfferModalDetails;
}

const ModalContent = ({ onChangeVisibility, modalDetails }: ModalExternalOfferRedeemProps) => {
  const { navigate, location } = useLocationContext();
  const { formatMessage } = useIntl();
  const cdp = useCdpContext();
  const now = useMemo(() => new Date().toString(), []);

  const handleCtaClick = () => {
    cdp.trackEvent({
      name: CustomEventNames.EXTERNAL_OFFER_REDEEMED,
      type: EventTypes.Other,
      attributes: {
        sanityId: modalDetails.sanityId,
        name: modalDetails.title,
        createdAt: now,
      },
    });

    const navigationUrl = modalDetails.buttonsConfiguration?.firstButton?.buttonPath?.locale;
    if (!navigationUrl) {
      return;
    }

    onChangeVisibility();

    navigate(navigationUrl);
  };

  return (
    <Container>
      <ImageWrapper>
        <OfferImage image={modalDetails?.image?.locale} alt="" objectFitContain />
      </ImageWrapper>
      <TextContentWrapper>
        <MainTitle>{modalDetails?.externalOfferModal?.title?.locale}</MainTitle>
        <OfferDescription>
          {blockContentToPlainText(modalDetails?.externalOfferModal?.description?.localeRaw)}{' '}
        </OfferDescription>
        <CTAButton
          onClick={handleCtaClick}
          aria-label="confirm-external-incentive-link"
          data-testid="confirm-external-incentive-link"
          href={modalDetails?.buttonsConfiguration?.firstButton?.buttonPath?.locale ?? ''}
          eventAttributes={{
            Name: 'Yes',
            Path: location.pathname,
          }}
        >
          {formatMessage({ id: 'yes' })}
        </CTAButton>
        <CloseButton
          onClick={() => onChangeVisibility()}
          aria-label="close-external-incentive-modal"
          data-testid="close-external-incentive-modal"
          variant={ActionButtonVariants.TEXT_ONLY}
          eventAttributes={{
            Name: 'Not Now',
            Path: location.pathname,
          }}
        >
          {formatMessage({ id: 'notNow' })}
        </CloseButton>
      </TextContentWrapper>
    </Container>
  );
};

export const ExternalIncentiveModal = ({
  onChangeVisibility: onChangeExternalOfferModalVisibility,
  modalVisibility,
  modalDetails,
}: ModalExternalOfferRedeemProps) => {
  const { location } = useLocationContext();
  if (!modalVisibility) {
    return null;
  }

  return (
    <Modal
      data-testid="external-offer-redeem-modal"
      backgroundColor="transparent"
      onDismiss={() => onChangeExternalOfferModalVisibility()}
      allowsDismiss={false}
      eventData={{
        Name: 'Access External Offer',
        Path: location.pathname,
        modalAppearanceEventMessage: 'Modal External Offers Redeem',
        modalMessage: 'Modal External Offers Redeem',
      }}
      size={ModalSize.REGULAR}
      overrideStyles={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <ModalContent
        onChangeVisibility={onChangeExternalOfferModalVisibility}
        modalDetails={modalDetails}
      />
    </Modal>
  );
};
